import { useEffect, useRef, useState } from "react";
import { ToolTip } from "../ToolTip";
import { ExclamationCircle } from "@/Icons";
import styles from "./TreeInput.module.scss";

interface TreeInputProps {
  errors: string[] | undefined;
  originalValue: string;
  saveHandler: (val: string) => Promise<void>;
}

export const TreeInput = ({
  originalValue,
  errors,
  saveHandler,
}: TreeInputProps) => {
  const [value, setValue] = useState<string>(originalValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState<boolean>(!!errors?.length);

  useEffect(() => {
    setShow(!!errors?.length);
  }, [errors, inputRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        saveHandler(value);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      saveHandler(value);
    }

    if (event.key === "Escape") {
      setValue(originalValue);
      saveHandler(originalValue);
    }
  };

  const errorsToolTip = () => {
    if (!errors) {
      return;
    }

    const rect = tooltipRef?.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    return (
      <ToolTip
        left={rect?.left - 90}
        top={rect?.top - (30 + errors!.length * 15)}
        messages={errors}
        type="ERROR"
      />
    );
  };

  return (
    <div className={`${styles["input-container"]}`}>
      <input
        type="text"
        ref={inputRef}
        className={`${errors?.length && styles["has-errors"]} `}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => saveHandler(value)}
        required={true}
        onKeyDown={handleKeyDown}
      />
      {errors?.length && (
        <i className={`${styles["icon-tooltip"]}`}>
          <span
            className={`icon-class ${styles["icon-error"]}`}
            ref={tooltipRef}
          >
            <ExclamationCircle />
          </span>
          {show && errorsToolTip()}
        </i>
      )}
    </div>
  );
};
